import React from 'react';
import { Clock, Gift, Percent, FileText, Sparkles, ArrowRight, Check, Coffee } from 'lucide-react';
import { features } from '../data/mock';
import { AnimateOnScroll } from '../hooks/useScrollAnimation';

const iconMap = {
  clock: Clock,
  gift: Gift,
  percent: Percent,
  invoice: FileText,
  coffee: Coffee
};

const Services = () => {
  return (
    <section id="services" className="section-padding bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9B1B30]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#006633]/5 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative">
        {/* Header */}
        <AnimateOnScroll animation="fadeUp">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-[#9B1B30]/10 text-[#9B1B30] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Sparkles size={16} />
              <span>Pourquoi nous choisir</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-6">
              <span className="block">Les avantages qui font</span>
              <span className="gradient-text block mt-3">la différence</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Une laverie moderne pensée pour vous. Lessive & Adoucissant OFFERTS, 
              tarifs transparents, qualité professionnelle.
            </p>
          </div>
        </AnimateOnScroll>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-20">
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <AnimateOnScroll key={feature.id} animation="fadeUp" delay={index * 100}>
                <div className="card-hover group bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 relative overflow-hidden border border-gray-100 h-full">
                  {/* Accent line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#9B1B30] to-[#006633] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  
                  <div className="w-16 h-16 bg-gradient-to-br from-[#9B1B30]/10 to-[#9B1B30]/5 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-[#9B1B30]" />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </AnimateOnScroll>
            );
          })}
        </div>

        {/* Commercial highlights */}
        <AnimateOnScroll animation="fadeUp">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { 
                title: "Qualité Professionnelle", 
                desc: "Machines dernière génération avec écran tactile",
                icon: "✨",
                color: "#9B1B30"
              },
              { 
                title: "Économique", 
                desc: "Lessive & Adoucissant OFFERTS + réductions fidélité",
                icon: "💰",
                color: "#006633"
              },
              { 
                title: "Confort", 
                desc: "Sièges, café et confiseries pendant l'attente",
                icon: "☕",
                color: "#9B1B30"
              }
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-gray-900 rounded-2xl p-6 text-white flex items-start gap-4 hover:scale-105 transition-transform"
              >
                <span className="text-3xl">{item.icon}</span>
                <div>
                  <h4 className="font-bold text-lg mb-1" style={{ color: item.color }}>{item.title}</h4>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimateOnScroll>

        {/* CTA Banner */}
        <AnimateOnScroll animation="scale">
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-[#9B1B30] rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#006633] rounded-full blur-3xl" />
            </div>
            
            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
              <div className="text-center lg:text-left">
                <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
                  Professionnels : jusqu'à <span className="text-[#9B1B30]">-30%</span> de réduction
                </h3>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {['Hôtels', 'Restaurants', 'Airbnb', 'Spas'].map((item, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-gray-300 text-sm">
                      <Check size={14} className="text-[#006633]" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <a
                href="#marches"
                className="group flex items-center gap-3 bg-[#9B1B30] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#B91C3B] transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Découvrir les offres
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default Services;
